// src/pages/CreateNewProduct.jsx

import React, { useState, useMemo } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import styles from './CreateNewProduct.module.css';
import { createProduct } from '../api/productApi';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';

const translations = {
  en: {
    pageTitle: 'Create New Product',
    formHeading: 'Product Details',
    name: 'Name',
    size: 'Size',
    sku: 'SKU',
    productImage: 'Product Image (Optional)',
    sortsHeading: 'Sorts',
    sortName: 'Sort Name',
    colorsQuantities: 'Colors & Quantities',
    addColor: '+ Add Color',
    removeColor: 'Remove',
    addSort: '+ Add Sort',
    removeSort: 'Remove Sort',
    createProduct: 'Create Product',
    cancel: 'Cancel',
    warning: 'Warning',
    productCreated: 'Product created successfully.',
    errorMessage: 'Failed to create product. Please try again.',
    close: 'Close',
    colorPlaceholder: 'Color name',
    quantityPlaceholder: 'Quantity',
    namePlaceholder: 'Enter product name',
    sizePlaceholder: 'Enter product size',
    skuPlaceholder: 'Enter unique SKU',
    sortNamePlaceholder: 'Enter sort name',
    homepage: 'Homepage',
  },
  ar: {
    pageTitle: 'إنشاء منتج جديد',
    formHeading: 'تفاصيل المنتج',
    name: 'الاسم',
    size: 'الحجم',
    sku: 'رمز المنتج (SKU)',
    productImage: 'صورة المنتج (اختياري)',
    sortsHeading: 'الأصناف',
    sortName: 'اسم الصنف',
    colorsQuantities: 'الألوان والكميات',
    addColor: '+ إضافة لون',
    removeColor: 'إزالة',
    addSort: '+ إضافة صنف',
    removeSort: 'إزالة الصنف',
    createProduct: 'إنشاء المنتج',
    cancel: 'إلغاء',
    warning: 'تحذير',
    productCreated: 'تم إنشاء المنتج بنجاح.',
    errorMessage: 'فشل في إنشاء المنتج. حاول مرة أخرى.',
    close: 'إغلاق',
    colorPlaceholder: 'اسم اللون',
    quantityPlaceholder: 'الكمية',
    namePlaceholder: 'أدخل اسم المنتج',
    sizePlaceholder: 'أدخل حجم المنتج',
    skuPlaceholder: 'أدخل رمز SKU فريد',
    sortNamePlaceholder: 'أدخل اسم الصنف',
    homepage: 'الصفحة الرئيسية',
  },
};

const CreateNewProduct = () => {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          userType: payload.userType,
          requesterUsername: payload.username,
        };
      } catch (error) {
        console.error('Error parsing token payload:', error);
        return { userType: null, requesterUsername: null };
      }
    }
    return { userType: null, requesterUsername: null };
  }, [token]);
  const [name, setName] = useState('');
  const [size, setSize] = useState('');
  const [SKU, setSKU] = useState('');
  const [sorts, setSorts] = useState([{ sortName: '', colors: [{ color: '', quantity: 0 }] }]);
  const [imageFile, setImageFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [language, setLanguage] = usePersistentLanguage();
  const [loading, setLoading] = useState(false);

  const t = (key) => translations[language][key];

  const handleAddSort = () => {
    setSorts((prev) => [...prev, { sortName: '', colors: [{ color: '', quantity: 0 }] }]);
  };

  const handleRemoveSort = (index) => {
    setSorts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSortChange = (index, field, value) => {
    setSorts((prev) =>
      prev.map((sort, i) => (i === index ? { ...sort, [field]: value } : sort))
    );
  };

  const handleAddColorToSort = (sortIndex) => {
    setSorts((prev) =>
      prev.map((sort, i) =>
        i === sortIndex
          ? { ...sort, colors: [...sort.colors, { color: '', quantity: 0 }] }
          : sort
      )
    );
  };

  const handleRemoveColorFromSort = (sortIndex, colorIndex) => {
    setSorts((prev) =>
      prev.map((sort, i) =>
        i === sortIndex
          ? { ...sort, colors: sort.colors.filter((_, cIdx) => cIdx !== colorIndex) }
          : sort
      )
    );
  };

  const handleColorChange = (sortIndex, colorIndex, field, value) => {
    setSorts((prev) =>
      prev.map((sort, i) => {
        if (i !== sortIndex) {
          return sort;
        }
        const colors = sort.colors.map((colorObj, cIdx) =>
          cIdx === colorIndex
            ? {
                ...colorObj,
                [field]: field === 'quantity' ? parseInt(value, 10) || 0 : value,
              }
            : colorObj
        );
        return { ...sort, colors };
      })
    );
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setImageFile(null);
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage(t('errorMessage'));
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage(t('errorMessage'));
      e.target.value = '';
      return;
    }
    setImageFile(file);
  };

  const resetForm = () => {
    setName('');
    setSize('');
    setSKU('');
    setSorts([{ sortName: '', colors: [{ color: '', quantity: 0 }] }]);
    setImageFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    const formData = new FormData();
    formData.append('name', name);
    formData.append('size', size);
    formData.append('SKU', SKU);
    formData.append('sorts', JSON.stringify(sorts));
    if (imageFile) {
      formData.append('image', imageFile);
    }

    try {
      setLoading(true);
      await createProduct(formData);
      setLoading(false);
      setSuccessMessage(t('productCreated'));
      resetForm();
    } catch (error) {
      console.error('Error creating product:', error);
      setErrorMessage(t('errorMessage'));
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <AppLayout
      title={t('pageTitle')}
      language={language}
      onToggleLanguage={() => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'))}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.createProductPage}
      contentProps={{ 'aria-busy': loading }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      {!loading && (
        <>
          {errorMessage && (
            <div className={`${styles.alert} ${styles.alertError}`} role="alert">
              <span>{errorMessage}</span>
              <button type="button" className={styles.alertDismiss} onClick={() => setErrorMessage(null)}>
                {t('close')}
              </button>
            </div>
          )}

          {successMessage && (
            <div className={`${styles.alert} ${styles.alertSuccess}`}>
              <span>{successMessage}</span>
              <button type="button" className={styles.alertDismiss} onClick={() => setSuccessMessage(null)}>
                {t('close')}
              </button>
            </div>
          )}

          <form className={styles.formCard} onSubmit={handleSubmit}>
            <h2 className={styles.sectionTitle}>{t('formHeading')}</h2>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="product-name">
                {t('name')}
              </label>
              <input
                id="product-name"
                className={styles.textInput}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder={t('namePlaceholder')}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="product-size">
                {t('size')}
              </label>
              <input
                id="product-size"
                className={styles.textInput}
                type="text"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                required
                placeholder={t('sizePlaceholder')}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="product-sku">
                {t('sku')}
              </label>
              <input
                id="product-sku"
                className={styles.textInput}
                type="text"
                value={SKU}
                onChange={(e) => setSKU(e.target.value)}
                required
                placeholder={t('skuPlaceholder')}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="product-image">
                {t('productImage')}
              </label>
              <input
                id="product-image"
                type="file"
                accept="image/*"
                className={styles.fileInput}
                onChange={handleImageChange}
              />
            </div>

            <h2 className={styles.sectionTitle}>{t('sortsHeading')}</h2>
            <div className={styles.sortList}>
              {sorts.map((sort, sortIndex) => (
                <div key={`sort-${sortIndex}`} className={styles.sortCard}>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`sort-name-${sortIndex}`}>
                      {t('sortName')}
                    </label>
                    <input
                      id={`sort-name-${sortIndex}`}
                      className={styles.textInput}
                      type="text"
                      value={sort.sortName}
                      onChange={(e) => handleSortChange(sortIndex, 'sortName', e.target.value)}
                      required
                      placeholder={t('sortNamePlaceholder')}
                    />
                  </div>

                  <div className={styles.colorList}>
                    <span className={styles.fieldLabel}>{t('colorsQuantities')}</span>
                    {sort.colors.map((colorObj, colorIndex) => (
                      <div key={`color-${sortIndex}-${colorIndex}`} className={styles.colorRow}>
                        <input
                          className={styles.colorInput}
                          type="text"
                          value={colorObj.color}
                          onChange={(e) => handleColorChange(sortIndex, colorIndex, 'color', e.target.value)}
                          placeholder={t('colorPlaceholder')}
                          required
                        />
                        <input
                          className={styles.quantityInput}
                          type="number"
                          min="0"
                          value={colorObj.quantity}
                          onChange={(e) => handleColorChange(sortIndex, colorIndex, 'quantity', e.target.value)}
                          placeholder={t('quantityPlaceholder')}
                          required
                        />
                        {sort.colors.length > 1 && (
                          <button
                            type="button"
                            className={styles.dangerButton}
                            onClick={() => handleRemoveColorFromSort(sortIndex, colorIndex)}
                          >
                            {t('removeColor')}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className={styles.controlButtons}>
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      onClick={() => handleAddColorToSort(sortIndex)}
                    >
                      {t('addColor')}
                    </button>
                    {sorts.length > 1 && (
                      <button
                        type="button"
                        className={styles.dangerButton}
                        onClick={() => handleRemoveSort(sortIndex)}
                      >
                        {t('removeSort')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button type="button" className={styles.primaryButton} onClick={handleAddSort}>
              {t('addSort')}
            </button>

            <div className={styles.footerButtons}>
              <button type="submit" className={styles.primaryButton}>
                {t('createProduct')}
              </button>
              <button type="button" className={styles.secondaryButton} onClick={handleCancel}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </>
      )}
    </AppLayout>
  );
};

export default CreateNewProduct;
